/** Pure path-deletion logic for the construction-line "Delete"
 *  sub-sub-tool. Three operations supported:
 *
 *  - **deleteSegment**: drop one curve between two adjacent
 *    anchors. Closed loops open at that point; open paths split
 *    into two (or one, if an endpoint segment is removed).
 *  - **deleteAnchor**: drop one anchor and merge its two
 *    bracketing segments into one. Closed loops stay closed; open
 *    paths shorten by one anchor at either end.
 *  - **deleteHandle**: zero one side (handleIn / handleOut) of an
 *    anchor's bezier handles, leaving the other side intact —
 *    "one-sided bezier on a point". Lock entries get cleared
 *    since asymmetric handles can't satisfy any lock.
 *
 *  All three return one or more `SegmentList` results; the
 *  paper-aware wrapper in `ConstructionTool` serialises each into
 *  pathData via paper.js. Pure here so the splitting math is
 *  unit-testable without a canvas. */

export type Point2 = { x: number; y: number };

/** Minimal serialisable description of a path segment — enough to
 *  rebuild the cubic curve in paper.js and to compare positions
 *  for tests. */
export type SegmentSpec = {
  point: Point2;
  handleIn: Point2;
  handleOut: Point2;
};

/** A single subpath worth of segments: ordered anchor list + a
 *  closed flag. The deletion functions emit one or more of these,
 *  each becoming its own ConstructionLine pathData. */
export type SegmentList = {
  segments: SegmentSpec[];
  closed: boolean;
};

const ZERO: Point2 = { x: 0, y: 0 };

const cloneSeg = (s: SegmentSpec): SegmentSpec => ({
  point: { x: s.point.x, y: s.point.y },
  handleIn: { x: s.handleIn.x, y: s.handleIn.y },
  handleOut: { x: s.handleOut.x, y: s.handleOut.y },
});

/** Delete one curve from the subpath. `curveIndex` is the curve
 *  whose endpoints are `segments[curveIndex]` and
 *  `segments[(curveIndex + 1) % segments.length]`.
 *
 *  Closed subpath → opens at the deleted curve. The result starts
 *  at the segment AFTER the deleted curve and walks around back to
 *  the segment BEFORE it.
 *
 *  Open subpath, interior curve → splits into two open paths.
 *  Open subpath, endpoint curve (first or last) → keeps one open
 *  path with the dangling endpoint dropped. A single-anchor result
 *  is filtered out (1-anchor "polylines" aren't valid). */
export function deleteSegment(
  list: SegmentList,
  curveIndex: number,
): SegmentList[] {
  const n = list.segments.length;
  if (n < 2) return [];
  if (list.closed) {
    if (curveIndex < 0 || curveIndex >= n) return [list];
    // Open the loop at this curve. Walk segments starting from the
    // one AFTER the deleted curve, wrapping around to the one
    // BEFORE it. The new start segment loses its handleIn (curve
    // it referred to is gone) and the new end loses its handleOut.
    const startIdx = (curveIndex + 1) % n;
    const out: SegmentSpec[] = [];
    for (let i = 0; i < n; i++) {
      const seg = cloneSeg(list.segments[(startIdx + i) % n]!);
      if (i === 0) seg.handleIn = { ...ZERO };
      if (i === n - 1) seg.handleOut = { ...ZERO };
      out.push(seg);
    }
    return [{ segments: out, closed: false }];
  }
  // Open subpath. curves[i] connects segments[i] and segments[i+1].
  if (curveIndex < 0 || curveIndex > n - 2) return [list];
  // Left side: segments [0 .. curveIndex] — its last segment loses
  // its handleOut (no curve continues from it).
  const left: SegmentSpec[] = list.segments
    .slice(0, curveIndex + 1)
    .map(cloneSeg);
  if (left.length > 0) left[left.length - 1]!.handleOut = { ...ZERO };
  // Right side: segments [curveIndex+1 .. n-1] — its first segment
  // loses its handleIn for the same reason.
  const right: SegmentSpec[] = list.segments
    .slice(curveIndex + 1)
    .map(cloneSeg);
  if (right.length > 0) right[0]!.handleIn = { ...ZERO };
  const results: SegmentList[] = [];
  if (left.length >= 2) results.push({ segments: left, closed: false });
  if (right.length >= 2) results.push({ segments: right, closed: false });
  return results;
}

/** Delete several curves at once, returning the surviving runs as
 *  open subpaths. Generalises `deleteSegment` for the swipe-delete
 *  knife (issue #670 follow-up), which may cross a line at more than
 *  one place. `curveIndices` are indices into the same curve numbering
 *  `deleteSegment` uses (curve `i` connects `segments[i]` and
 *  `segments[(i + 1) % n]`).
 *
 *  Consecutive surviving curves stay joined into one run; each gap
 *  (deleted curve) ends a run, with the severed handles zeroed on
 *  both sides. A closed loop opens into one or more runs. Runs of
 *  fewer than two anchors are dropped. With a single index this
 *  matches `deleteSegment`. */
export function deleteCurves(
  list: SegmentList,
  curveIndices: number[],
): SegmentList[] {
  const n = list.segments.length;
  if (n < 2) return [];
  const curveCount = list.closed ? n : n - 1;
  const del = new Set<number>();
  for (const i of curveIndices) {
    if (i >= 0 && i < curveCount) del.add(i);
  }
  if (del.size === 0) return [list];

  const runs: SegmentList[] = [];
  const pushRun = (run: SegmentSpec[]) => {
    if (run.length >= 2) runs.push({ segments: run, closed: false });
  };

  if (!list.closed) {
    let cur: SegmentSpec[] = [];
    for (let i = 0; i < curveCount; i++) {
      if (del.has(i)) {
        if (cur.length > 0) cur[cur.length - 1]!.handleOut = { ...ZERO };
        pushRun(cur);
        cur = [];
        continue;
      }
      if (cur.length === 0) {
        const a = cloneSeg(list.segments[i]!);
        if (i > 0 && del.has(i - 1)) a.handleIn = { ...ZERO };
        cur.push(a);
      }
      cur.push(cloneSeg(list.segments[i + 1]!));
    }
    pushRun(cur);
    return runs;
  }

  // Closed: start walking from the curve right after the first deleted
  // one, so no run straddles a deletion across the seam.
  const startCurve = [...del].sort((a, b) => a - b)[0]!;
  let cur: SegmentSpec[] = [];
  for (let k = 1; k <= curveCount; k++) {
    const i = (startCurve + k) % curveCount;
    if (i === startCurve) break;
    if (del.has(i)) {
      if (cur.length > 0) cur[cur.length - 1]!.handleOut = { ...ZERO };
      pushRun(cur);
      cur = [];
      continue;
    }
    if (cur.length === 0) {
      const a = cloneSeg(list.segments[i]!);
      a.handleIn = { ...ZERO };
      cur.push(a);
    }
    cur.push(cloneSeg(list.segments[(i + 1) % n]!));
  }
  if (cur.length > 0) cur[cur.length - 1]!.handleOut = { ...ZERO };
  pushRun(cur);
  return runs;
}

/** Delete one anchor. Closed loops stay closed (one fewer
 *  segment). Open paths drop the anchor and the cycle's two
 *  bracketing segments collapse into a single direct line; the
 *  surviving anchors keep their existing handles on the *outside*
 *  sides (away from the deleted anchor) and lose the *inside*
 *  handles (the ones that used to point toward the deleted
 *  anchor). Reduces to fewer than 2 segments → empty result. */
export function deleteAnchor(
  list: SegmentList,
  segmentIndex: number,
): SegmentList[] {
  const n = list.segments.length;
  if (n === 0) return [];
  if (segmentIndex < 0 || segmentIndex >= n) return [list];
  const remaining: SegmentSpec[] = [];
  for (let i = 0; i < n; i++) {
    if (i === segmentIndex) continue;
    remaining.push(cloneSeg(list.segments[i]!));
  }
  if (remaining.length === 0) return [];
  if (list.closed) {
    if (remaining.length < 3) {
      // A 2-segment closed loop is degenerate; collapse to open.
      if (remaining.length < 2) return [];
      // Drop handles facing the removed anchor.
      remaining[0]!.handleIn = { ...ZERO };
      remaining[remaining.length - 1]!.handleOut = { ...ZERO };
      return [{ segments: remaining, closed: false }];
    }
    // Closed: handles on the two anchors that used to bracket the
    // deleted one lose the side that pointed toward it.
    const beforeIdx = (segmentIndex - 1 + n) % n; // index in original
    const afterIdx = (segmentIndex + 1) % n; // index in original
    // Translate to indices in `remaining`.
    const remainingIndexOf = (origIdx: number) =>
      origIdx > segmentIndex ? origIdx - 1 : origIdx;
    remaining[remainingIndexOf(beforeIdx)]!.handleOut = { ...ZERO };
    remaining[remainingIndexOf(afterIdx)]!.handleIn = { ...ZERO };
    return [{ segments: remaining, closed: true }];
  }
  // Open path. If the deleted anchor was an endpoint, just trim;
  // if interior, the two bracketing segments merge into one.
  if (remaining.length < 2) return [];
  if (segmentIndex === 0) {
    // First anchor removed — new first anchor loses its handleIn.
    remaining[0]!.handleIn = { ...ZERO };
  } else if (segmentIndex === n - 1) {
    // Last anchor removed — new last anchor loses its handleOut.
    remaining[remaining.length - 1]!.handleOut = { ...ZERO };
  } else {
    // Interior: anchors at indices (segmentIndex - 1) and
    // (segmentIndex + 1) bracketed the deleted anchor. After
    // removal they sit at adjacent indices (segmentIndex - 1 and
    // segmentIndex). The handle FACING the gap on each gets
    // zeroed.
    remaining[segmentIndex - 1]!.handleOut = { ...ZERO };
    remaining[segmentIndex]!.handleIn = { ...ZERO };
  }
  return [{ segments: remaining, closed: false }];
}

/** Zero one side of one anchor's bezier handles. Doesn't change
 *  the path topology — the segment list is the same length. The
 *  anchor's lock entry (smoothAnchors) should be cleared by the
 *  caller since one-sided handles can't satisfy any lock. */
export function deleteHandle(
  list: SegmentList,
  segmentIndex: number,
  side: "in" | "out",
): SegmentList[] {
  const n = list.segments.length;
  if (n === 0) return [list];
  if (segmentIndex < 0 || segmentIndex >= n) return [list];
  const segs = list.segments.map(cloneSeg);
  if (side === "in") {
    segs[segmentIndex]!.handleIn = { ...ZERO };
  } else {
    segs[segmentIndex]!.handleOut = { ...ZERO };
  }
  return [{ segments: segs, closed: list.closed }];
}
